import { createOAuthTransaction, exchangeOAuth, OAuthProvider } from '../api/oauthApi';
import { LoginResponse } from '../api/authApi';
import { extractUserIdFromToken, getSessionRevision, getUserId } from './tokenStore';

const PENDING_KEY = 'sc_oauth_pending';
const MAX_AGE_MS = 15 * 60 * 1000;
const CONSUMED_KEY = 'sc_oauth_consumed';
interface PendingTransaction {
  transactionId: string;
  codeVerifier: string;
  createdAt: number;
  destination: string;
  intent: 'login' | 'link';
  userId: string | null;
}

export function safeDestination(value: unknown, fallback = '/documents'): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || /[\\\s]/.test(value)) {
    return fallback;
  }
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin || /^\/(?:login|auth\/callback)(?:\/|$)/.test(url.pathname)) return fallback;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}

function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...Array.from(bytes))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function createPkce(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  if (!window.crypto?.subtle) throw new Error('Social sign-in requires a secure HTTPS connection (or localhost).');
  const codeVerifier = base64url(window.crypto.getRandomValues(new Uint8Array(32)));
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  return { codeVerifier, codeChallenge: base64url(new Uint8Array(digest)) };
}

export async function startOAuth(
  provider: OAuthProvider,
  destination: string,
  intent: 'login' | 'link' = 'login',
): Promise<void> {
  const { codeVerifier, codeChallenge } = await createPkce();
  const userId = getUserId();
  if (intent === 'link' && !userId) throw new Error('Sign in before linking an account.');
  try {
    sessionStorage.setItem(PENDING_KEY, '');
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    throw new Error('Allow session storage in this browser to continue with social sign-in.');
  }
  const transaction = await createOAuthTransaction({ provider, codeChallenge, intent });
  const authorizationUrl = new URL(transaction.authorizationUrl);
  const expectedHost = provider === 'google' ? 'accounts.google.com' : 'appleid.apple.com';
  if (!transaction.transactionId || authorizationUrl.protocol !== 'https:' || authorizationUrl.hostname !== expectedHost) {
    throw new Error('The sign-in service returned an invalid provider redirect. Please try again.');
  }
  const pending: PendingTransaction = {
    transactionId: transaction.transactionId, codeVerifier, createdAt: Date.now(),
    destination: safeDestination(destination), intent, userId: intent === 'link' ? userId : null,
  };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  window.location.assign(authorizationUrl.toString());
}

const CALLBACK_ERRORS: Record<string, string> = {
  access_denied: 'Sign-in was cancelled. Please try again or use email and password.',
  provider_unavailable: 'This provider is not available. Please use another sign-in method.',
  account_link_required: 'This provider could not be linked automatically. Sign in with your existing method, then manage the provider in Account settings. If a different provider account is already linked, use that account or contact support.',
  invalid_transaction: 'This sign-in attempt expired or was already used. Please start again.',
  auth_failed: 'The provider could not verify your account. Please try again.',
};

export async function completeOAuth(search: string): Promise<{ session: LoginResponse; destination: string; expectedUserId: string | null }> {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    throw new Error('Allow session storage in this browser, then start social sign-in again.');
  }
  let pending: PendingTransaction | null = null;
  try { pending = raw ? JSON.parse(raw) : null; } catch { /* Invalid pending state is rejected below. */ }
  const params = new URLSearchParams(search);
  if (!pending || typeof pending.transactionId !== 'string' || !pending.transactionId
      || (pending.intent !== 'login' && pending.intent !== 'link')
      || typeof pending.codeVerifier !== 'string' || !/^[A-Za-z0-9_-]{43,128}$/.test(pending.codeVerifier)
      || !Number.isFinite(pending.createdAt) || Date.now() - pending.createdAt > MAX_AGE_MS
      || pending.createdAt > Date.now() || params.get('transaction_id') !== pending.transactionId) {
    throw new Error(CALLBACK_ERRORS.invalid_transaction);
  }
  // sessionStorage can be cloned when opening a duplicate tab. Keep a bounded,
  // non-secret cross-tab replay hint; the backend remains the atomic authority.
  try {
    let consumed: { id: string; at: number }[] = [];
    try {
      const previous: unknown = JSON.parse(localStorage.getItem(CONSUMED_KEY) || '[]');
      if (Array.isArray(previous)) consumed = previous.filter((entry) =>
        entry && typeof entry.id === 'string' && typeof entry.at === 'number' && Date.now() - entry.at < MAX_AGE_MS);
    } catch { /* Replace malformed replay hints. */ }
    if (consumed.some((entry) => entry.id === pending!.transactionId)) throw new Error(CALLBACK_ERRORS.invalid_transaction);
    localStorage.setItem(CONSUMED_KEY, JSON.stringify([...consumed.slice(-19), { id: pending.transactionId, at: Date.now() }]));
  } catch (reason) {
    if (reason instanceof Error && reason.message === CALLBACK_ERRORS.invalid_transaction) throw reason;
    throw new Error('Allow browser storage, then start social sign-in again.');
  }
  const error = params.get('error');
  if (error) throw new Error(CALLBACK_ERRORS[error] || 'Social sign-in failed. Please start again or use email and password.');
  const code = params.get('code');
  if (!code) throw new Error('The sign-in response is missing a completion code. Please start again.');
  if (pending.intent === 'link' && (!pending.userId || pending.userId !== getUserId())) {
    throw new Error('Your signed-in account changed. Please sign in again before linking.');
  }
  const revision = getSessionRevision();
  const session = await exchangeOAuth({ transactionId: pending.transactionId, code, codeVerifier: pending.codeVerifier });
  if (revision !== getSessionRevision() || (pending.intent === 'link' && getUserId() !== pending.userId)) {
    throw new Error('Your session changed during sign-in. Please start again.');
  }
  if (pending.intent === 'link' && !session.requiresMfa
      && String(session.userId ?? (session.accessToken ? extractUserIdFromToken(session.accessToken) : null)) !== pending.userId) {
    throw new Error('The linked account did not match your session. Please sign in again.');
  }
  return { session, destination: safeDestination(pending.destination), expectedUserId: pending.intent === 'link' ? pending.userId : null };
}

export const MFA_MESSAGE = 'Additional verification is required. This web app cannot complete MFA yet. Sign in using a supported Docuhounds client to complete verification, or contact support for help with account access.';
